import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Users, X, Check } from 'lucide-react';

export default function DomainAutoJoinBanner() {
  const [eligibleTeams, setEligibleTeams] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [joinedTeam, setJoinedTeam] = useState(null);

  useEffect(() => {
    // Check localStorage for eligible teams on component mount
    const storedTeams = localStorage.getItem('eligibleTeams');
    if (storedTeams) {
      try {
        const teams = JSON.parse(storedTeams);
        if (teams.length > 0) {
          setEligibleTeams(teams);
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Error parsing stored eligible teams:', error);
      }
    }

    // Listen for the custom event from AuthContext
    const handleDomainAutoJoin = (event) => {
      setEligibleTeams(event.detail);
      setIsVisible(true);
    };

    window.addEventListener('domainAutoJoinAvailable', handleDomainAutoJoin);

    return () => {
      window.removeEventListener('domainAutoJoinAvailable', handleDomainAutoJoin);
    };
  }, []);

  const joinTeam = async (teamId) => {
    setIsJoining(true);
    try {
      const autoJoinTeam = httpsCallable(functions, 'auto_join_team');
      const result = await autoJoinTeam({ teamId });

      if (result.data.success) {
        setJoinedTeam(result.data);
        // Remove from localStorage
        localStorage.removeItem('eligibleTeams');
        // Auto-hide after showing success
        setTimeout(() => {
          setIsVisible(false);
        }, 3000);
        // Refresh the page to update membership context
        setTimeout(() => {
          window.location.reload();
        }, 3500);
      }
    } catch (error) {
      console.error('Error joining team:', error);
      alert('Failed to join team. Please try again.');
    } finally {
      setIsJoining(false);
    }
  };

  const dismissBanner = () => {
    setIsVisible(false);
    localStorage.removeItem('eligibleTeams');
  };

  if (!isVisible || eligibleTeams.length === 0) {
    return null;
  }

  if (joinedTeam) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <Check className="h-5 w-5 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              Successfully joined {joinedTeam.teamName}!
            </p>
            <p className="text-xs text-green-600 mt-1">
              Refreshing page to update your membership...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <Users className="h-5 w-5 text-blue-600 mt-0.5" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            You're eligible to join a team!
          </h3>
          <p className="text-sm text-blue-700 mb-3">
            Based on your email domain, you can automatically join the following team{eligibleTeams.length > 1 ? 's' : ''}:
          </p>

          <div className="space-y-2">
            {eligibleTeams.map((team) => (
              <div key={team.teamId} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-200">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{team.teamName}</p>
                  <p className="text-xs text-gray-600">
                    {team.memberCount} members • {team.availableSeats} seats available
                  </p>
                </div>
                <button
                  onClick={() => joinTeam(team.teamId)}
                  disabled={isJoining}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isJoining ? 'Joining...' : 'Join Team'}
                </button>
              </div>
            ))}
          </div>

          <p className="text-xs text-blue-600 mt-3">
            You can dismiss this notification if you prefer not to join any team.
          </p>
        </div>
        <button
          onClick={dismissBanner}
          className="flex-shrink-0 text-blue-400 hover:text-blue-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}